import { queryStorefront } from 'swatches';

/**
 * Algolia's `price`/`compare_at_price` fields are captured once, in a single
 * default-market currency, at index time — they never reflect the shopper's
 * active market. Shopify's Storefront API does, via `@inContext(country:)`,
 * the same mechanism swatches.js already uses to price variant product
 * cards correctly, so this overlays real current-market prices onto Algolia
 * hits before they're rendered.
 */

const PRICE_QUERY_BODY = `
  nodes {
    handle
    priceRange {
      minVariantPrice { amount }
    }
    compareAtPriceRange {
      minVariantPrice { amount }
    }
  }
`;

// Module-scoped so autocomplete and the SRP share one cache for the page's
// lifetime — a handle priced once doesn't need to be re-fetched.
const marketPriceCache = {};

async function fetchMarketPrices(handles) {
  if (!handles.length) return;

  const query = handles.map(handle => `handle:'${handle}'`).join(' OR ');

  try {
    const products = await queryStorefront({
      type: 'products',
      params: { query, first: handles.length },
      body: PRICE_QUERY_BODY,
    });

    for (const product of products || []) {
      const price = parseFloat(product.priceRange.minVariantPrice.amount);
      const compareAtPrice = parseFloat(product.compareAtPriceRange.minVariantPrice.amount);

      marketPriceCache[product.handle] = {
        price,
        compare_at_price: compareAtPrice > price ? compareAtPrice : null,
      };
    }
  } catch (err) {
    console.error('[Search] Market pricing fetch failed:', err);
    // Non-fatal — hits whose handle never lands in the cache just keep
    // showing their original indexed price/currency.
  }
}

/**
 * Overlays current-market price/compare_at_price onto Algolia hits, in
 * place, fetching any handles not already cached this session.
 *
 * @param {Array} hits - Algolia product hits (mutated in place).
 * @returns {Promise<Array>} the same hits array, for convenience chaining.
 */
export async function applyMarketPricing(hits) {
  const handles = [...new Set(hits.map(hit => hit.handle).filter(Boolean))]
    .filter(handle => !(handle in marketPriceCache));

  if (handles.length) await fetchMarketPrices(handles);

  for (const hit of hits) {
    const marketPrice = marketPriceCache[hit.handle];
    if (marketPrice) {
      hit.price = marketPrice.price;
      hit.compare_at_price = marketPrice.compare_at_price;
    }
  }

  return hits;
}
